import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { 
  Mail, Settings, Play, CheckCircle, XCircle, Clock, 
  FileText, AlertTriangle, RefreshCw, Eye, Server
} from "lucide-react";
import axios from "axios";

const EmailScanner = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  
  const [formData, setFormData] = useState({
    server: "",
    port: 993,
    username: "",
    password: "",
    folder: "INBOX"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [configRes, historyRes] = await Promise.all([
        axios.get(`${API}/email-scanner/config`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/email-scanner/history`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setConfig(configRes.data);
      setHistory(historyRes.data);
      
      if (configRes.data.is_configured) {
        setFormData({
          server: configRes.data.server || "",
          port: configRes.data.port || 993,
          username: configRes.data.username || "",
          password: "",
          folder: configRes.data.folder || "INBOX"
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!formData.server || !formData.username || !formData.password) {
      toast.error("Fyll i alla obligatoriska fält");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/email-scanner/config`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Konfiguration sparad!");
      setShowConfig(false);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || "Kunde inte spara konfiguration";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async () => {
    try {
      await axios.put(`${API}/email-scanner/config`, 
        { is_enabled: !config?.is_enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(config?.is_enabled ? "E-postskanner inaktiverad" : "E-postskanner aktiverad");
      fetchData();
    } catch (error) {
      toast.error("Kunde inte uppdatera status");
    }
  };

  const handleScanNow = async () => {
    setScanning(true);
    try {
      const response = await axios.post(`${API}/email-scanner/scan`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { processed, errors } = response.data;
      
      if (processed.length > 0) {
        toast.success(`${processed.length} fakturor hittades och bearbetades!`);
      } else {
        toast.info("Inga nya fakturor hittades i inkorgen");
      }
      
      if (errors.length > 0) {
        toast.warning(`${errors.length} e-postmeddelanden kunde inte bearbetas`);
      }
      
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || "Kunde inte skanna e-post";
      toast.error(detail);
    } finally {
      setScanning(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "-";
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-green-100 text-green-700">Godkänd</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-700">Avvisad</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700">Väntar</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="email-scanner">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">E-postskanner</h1>
          <p className="text-slate-500 mt-1">Automatisk fakturaskanning från e-post (IMAP)</p>
        </div>
        <div className="flex gap-3">
          {config?.is_configured && (
            <>
              <Button 
                variant="outline" 
                onClick={() => setShowConfig(!showConfig)}
                data-testid="config-btn"
              >
                <Settings className="mr-2 h-4 w-4" />
                Inställningar
              </Button>
              <Button 
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleScanNow}
                disabled={scanning || !config?.is_enabled}
                data-testid="scan-now-btn"
              >
                {scanning ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-2 h-4 w-4" />
                )}
                Skanna nu
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Configuration Section */}
      {(!config?.is_configured || showConfig) && (
        <Card className="card-base" data-testid="config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              IMAP-konfiguration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>IMAP-server *</Label>
                <Input 
                  value={formData.server}
                  onChange={(e) => setFormData({...formData, server: e.target.value})}
                  placeholder="mail.yubi.se"
                  data-testid="imap-server"
                />
              </div>
              <div className="space-y-2">
                <Label>Port</Label>
                <Input 
                  type="number"
                  value={formData.port}
                  onChange={(e) => setFormData({...formData, port: parseInt(e.target.value) || 993})}
                  placeholder="993"
                  data-testid="imap-port"
                />
              </div>
              <div className="space-y-2">
                <Label>Användarnamn (e-post) *</Label>
                <Input 
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  placeholder="invoices@yubi.se"
                  data-testid="imap-username"
                />
              </div>
              <div className="space-y-2">
                <Label>Lösenord *</Label>
                <Input 
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="••••••••"
                  data-testid="imap-password"
                />
              </div>
              <div className="space-y-2">
                <Label>Mapp</Label>
                <Input 
                  value={formData.folder}
                  onChange={(e) => setFormData({...formData, folder: e.target.value})}
                  placeholder="INBOX"
                  data-testid="imap-folder"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              {showConfig && (
                <Button variant="outline" onClick={() => setShowConfig(false)}>
                  Avbryt
                </Button>
              )}
              <Button 
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleSaveConfig}
                disabled={saving}
                data-testid="save-config-btn"
              >
                {saving ? "Sparar..." : "Spara & testa anslutning"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Card */}
      {config?.is_configured && (
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${config.is_enabled ? 'bg-green-100' : 'bg-slate-100'}`}>
                  <Mail className={`h-6 w-6 ${config.is_enabled ? 'text-green-600' : 'text-slate-500'}`} />
                </div>
                <div>
                  <p className="font-medium text-slate-900">
                    {config.username}
                  </p>
                  <p className="text-sm text-slate-500">
                    {config.server}:{config.port} / {config.folder}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Aktiverad</span>
                  <Switch 
                    checked={config.is_enabled}
                    onCheckedChange={handleToggleEnabled}
                    data-testid="toggle-enabled"
                  />
                </div>
                <Badge className={config.is_enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
                  {config.is_enabled ? "Aktiv" : "Inaktiv"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History List */}
      <Card className="card-base" data-testid="history-list">
        <CardHeader>
          <CardTitle className="text-lg">Skannade fakturor från e-post</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {history.length > 0 ? (
            <div className="divide-y divide-slate-200">
              {history.map((invoice) => (
                <div key={invoice.id} className="p-4 hover:bg-slate-50" data-testid={`invoice-${invoice.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-slate-100">
                        <FileText className="h-5 w-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{invoice.vendor || invoice.filename}</p>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <span>{invoice.invoice_number || "-"}</span>
                          <span>•</span>
                          <span>{invoice.invoice_date || "-"}</span>
                          {invoice.email_sender && (
                            <>
                              <span>•</span>
                              <span className="text-xs">Från: {invoice.email_sender}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-slate-900">{formatCurrency(invoice.total_amount)}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(invoice.created_at).toLocaleDateString("sv-SE")}
                        </p>
                      </div>
                      {getStatusBadge(invoice.status)}
                    </div>
                  </div>
                  
                  {invoice.email_subject && (
                    <p className="mt-2 text-xs text-slate-400 pl-14 truncate">
                      Ämne: {invoice.email_subject}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 text-slate-500">
              <Mail className="mx-auto mb-4 text-slate-300" size={48} />
              <p>Inga fakturor skannade från e-post ännu</p>
              <p className="text-sm mt-1">
                {config?.is_configured 
                  ? 'Klicka på "Skanna nu" för att hämta fakturor från din inkorg'
                  : 'Konfigurera IMAP-anslutning ovan för att komma igång'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Så fungerar e-postskanningen</p>
              <p className="text-sm text-blue-700 mt-1">
                1. Skapa en dedikerad e-postadress (t.ex. fakturor@yubi.se)<br/>
                2. Konfigurera IMAP-anslutningen ovan<br/>
                3. Vidarebefordra leverantörsfakturor till denna adress<br/>
                4. PDF-bilagor skannas automatiskt med AI och importeras till Fakturaskannern
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailScanner;
